/**
 * Landing page — Monkey Barber Club
 * Black canvas, ink-on-surface hierarchy, a single brand-yellow accent.
 * See DESIGN.md — this page must read tokens, never invent color/gradient/emoji.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { PublicNavbar } from '@/components/layout/PublicNavbar';
import { PublicFooter } from '@/components/layout/PublicFooter';
import { OptimizedImage } from '@/components/common/OptimizedImage';
import { Button } from '@/components/ui/Button';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Branch, Service } from '@/types';
import { MONKEY_FRANCHISE_ID } from '@/lib/config';
import { JobApplicationModal } from '@/components/modals/JobApplicationModal';
import { FranchiseApplicationModal } from '@/components/modals/FranchiseApplicationModal';
import { SectionHeading, getServiceIcon } from '@/components/layout/PublicSections';
import {
  ArrowRight, MapPin, Scissors,
  Smartphone, Bell, Clock, Zap, ShieldCheck, Target, Lock,
  Briefcase, Users, Sparkles, Heart, Store, TrendingUp, Shield, Check,
  type LucideIcon,
} from 'lucide-react';

// Statistics interface for landing page
interface LandingStats {
  totalClients: number;
  totalBarbers: number;
  avgRating: number;
  avgWaitTime: number;
}

// Default schedule for fallback
const DEFAULT_SCHEDULE = {
  monday: { label: 'Lunes', time: '10:00 - 21:00', isOpen: true },
  tuesday: { label: 'Martes', time: '10:00 - 21:00', isOpen: true },
  wednesday: { label: 'Miércoles', time: '10:00 - 21:00', isOpen: true },
  thursday: { label: 'Jueves', time: '10:00 - 21:00', isOpen: true },
  friday: { label: 'Viernes', time: '10:00 - 21:00', isOpen: true },
  saturday: { label: 'Sábado', time: '10:00 - 20:00', isOpen: true },
  sunday: { label: 'Domingo', time: 'Cerrado', isOpen: false }
};

// Helper function to format branch schedule
function formatSchedule(branch: Branch | null) {
  if (!branch || !branch.schedule) {
    return DEFAULT_SCHEDULE;
  }

  const { schedule } = branch;

  // Helper to safely get day schedule
  const formatDay = (day: keyof typeof schedule, label: string) => {
    const daySchedule = schedule[day];
    if (!daySchedule) {
      return { label, time: '10:00 - 21:00', isOpen: true };
    }
    return {
      label,
      time: daySchedule.isOpen ? `${daySchedule.open} - ${daySchedule.close}` : 'Cerrado',
      isOpen: daySchedule.isOpen
    };
  };

  return {
    monday: formatDay('monday', 'Lunes'),
    tuesday: formatDay('tuesday', 'Martes'),
    wednesday: formatDay('wednesday', 'Miércoles'),
    thursday: formatDay('thursday', 'Jueves'),
    friday: formatDay('friday', 'Viernes'),
    saturday: formatDay('saturday', 'Sábado'),
    sunday: formatDay('sunday', 'Domingo')
  };
}

// Order matches Date.getDay() (0 = Sunday) so we can highlight today's row.
const DAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

export default function Home() {
  const { t } = useTranslation();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [stats, setStats] = useState<LandingStats>({
    totalClients: 0, // Loaded from database
    totalBarbers: 0,
    avgRating: 4.9,
    avgWaitTime: 15,
  });
  const [showJobModal, setShowJobModal] = useState(false);
  const [showFranchiseModal, setShowFranchiseModal] = useState(false);
  const [activeOpportunityTab, setActiveOpportunityTab] = useState<'job' | 'franchise'>('job');

  // Real-time listener for landing page statistics from public_stats collection
  useEffect(() => {
    const statsDocRef = doc(db, 'public_stats', MONKEY_FRANCHISE_ID);

    const unsubscribe = onSnapshot(statsDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setStats({
          totalClients: data.totalClients || 0,
          totalBarbers: data.totalBarbers || 0,
          avgRating: 4.9, // Fixed rating for now
          avgWaitTime: data.avgWaitTime || 15,
        });
      }
    }, (error) => {
      console.error('Error listening to landing stats:', error);
    });

    return () => unsubscribe();
  }, []);

  // Real-time listener for active branches
  useEffect(() => {
    const q = query(
      collection(db, 'branches'),
      where('isActive', '==', true),
      where('franchiseId', '==', MONKEY_FRANCHISE_ID)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      try {
        const branchesList = snapshot.docs.map(doc => ({
          branchId: doc.id,
          ...doc.data(),
        })) as Branch[];

        setBranches(branchesList);

        // If no branch is selected yet, select the first one
        if (!selectedBranchId && branchesList.length > 0) {
          setSelectedBranchId(branchesList[0].branchId);
        }
      } catch (error) {
        console.error('Error loading branches:', error);
      }
    }, (error) => {
      console.error('Error listening to branches:', error);
    });

    return () => unsubscribe();
  }, [selectedBranchId]);

  // Real-time listener for services based on selected branch
  useEffect(() => {
    if (!selectedBranchId) return;

    // Query services for the selected branch, or franchise-wide if no branchId
    const q = query(
      collection(db, 'services'),
      where('franchiseId', '==', MONKEY_FRANCHISE_ID),
      where('isActive', '==', true)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      try {
        const servicesList = snapshot.docs.map(doc => ({
          serviceId: doc.id,
          ...doc.data(),
        })) as Service[];

        // Sort by displayOrder (set from admin), then by name as fallback
        const sortedServices = servicesList.sort((a, b) => {
          const orderA = a.displayOrder ?? 999;
          const orderB = b.displayOrder ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return a.name.localeCompare(b.name);
        });

        setServices(sortedServices);
      } catch (error) {
        console.error('Error loading services:', error);
      }
    }, (error) => {
      console.error('Error listening to services:', error);
    });

    return () => unsubscribe();
  }, [selectedBranchId]);

  // Get the currently selected branch object
  const selectedBranch = branches.find(b => b.branchId === selectedBranchId) || null;

  // Format the schedule for display
  const scheduleDisplay = formatSchedule(selectedBranch);
  const todayKey = DAY_ORDER[new Date().getDay()];

  // Neutral, formatted client count (ink, never yellow — see DESIGN.md)
  const clientsLabel = stats.totalClients >= 1000
    ? `${Math.floor(stats.totalClients / 1000)}K+`
    : `${stats.totalClients}+`;

  // How-it-works steps and feature tiles, declared once for clean mapping.
  const steps: { num: string; Icon: LucideIcon; titleKey: string; descKey: string }[] = [
    { num: '01', Icon: Smartphone, titleKey: 'home.howItWorks.step1.title', descKey: 'home.howItWorks.step1.description' },
    { num: '02', Icon: Bell, titleKey: 'home.howItWorks.step2.title', descKey: 'home.howItWorks.step2.description' },
    { num: '03', Icon: Scissors, titleKey: 'home.howItWorks.step3.title', descKey: 'home.howItWorks.step3.description' },
  ];

  const features: { Icon: LucideIcon; titleKey: string; descKey: string }[] = [
    { Icon: Zap, titleKey: 'home.features.fast.title', descKey: 'home.features.fast.description' },
    { Icon: ShieldCheck, titleKey: 'home.features.reliable.title', descKey: 'home.features.reliable.description' },
    { Icon: Target, titleKey: 'home.features.precise.title', descKey: 'home.features.precise.description' },
    { Icon: Lock, titleKey: 'home.features.secure.title', descKey: 'home.features.secure.description' },
  ];

  // JSON-LD structured data for local business SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BarberShop",
    "name": "Monkey Barber Club",
    "image": "https://monkeybarberclub.com/logo.png",
    "description": t('home.seo.description'),
    "url": "https://monkeybarberclub.com",
    "telephone": "+34604216330",
    "email": "Leandro.usc@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "ES",
      "addressLocality": "Madrid"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "10:00",
        "closes": "21:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "10:00",
        "closes": "20:00"
      }
    ],
    "priceRange": "€€",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "5000"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": t('home.services.title'),
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": t('home.services.classic.name'),
            "description": t('home.services.classic.description')
          },
          "price": "15",
          "priceCurrency": "EUR"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": t('home.services.combo.name'),
            "description": t('home.services.combo.description')
          },
          "price": "25",
          "priceCurrency": "EUR"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": t('home.services.vip.name'),
            "description": t('home.services.vip.description')
          },
          "price": "40",
          "priceCurrency": "EUR"
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <PublicNavbar />

      <Helmet>
        <title>{t('home.seo.title')}</title>
        <meta name="description" content={t('home.seo.description')} />
        <meta name="keywords" content={t('home.seo.keywords')} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://monkeybarberclub.com/" />
        <meta property="og:title" content={t('home.seo.ogTitle')} />
        <meta property="og:description" content={t('home.seo.ogDescription')} />
        <meta property="og:image" content="https://monkeybarberclub.com/og-image.jpg" />
        <meta property="og:locale" content="es_ES" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://monkeybarberclub.com/" />
        <meta name="twitter:title" content={t('home.seo.ogTitle')} />
        <meta name="twitter:description" content={t('home.seo.ogDescription')} />
        <meta name="twitter:image" content="https://monkeybarberclub.com/twitter-image.jpg" />

        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="Spanish" />
        <meta name="author" content="Monkey Barber Club" />
        <link rel="canonical" href="https://monkeybarberclub.com/" />

        {/* JSON-LD structured data */}
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* ── Hero ───────────────────────────────────────────────── */}
      {/* Top padding lives on the <section> (not the .container, whose top
          padding is overridden by a global mobile rule) so the fixed navbar
          never clips the eyebrow / wordmark. */}
      <section className="relative overflow-hidden border-b border-hairline bg-canvas pt-32 md:pt-40">
        <div className="container mx-auto px-6 pb-20 md:pb-28">
          <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            {/* Copy */}
            <div>
              <p className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-ink-subtle">
                <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
                {t('home.hero.tagline')}
              </p>

              <h1 className="mt-6 leading-[0.92] tracking-tighter">
                <span className="block font-display text-6xl text-accent md:text-8xl">{t('home.hero.title')}</span>
                <span className="mt-2 block font-sans text-3xl font-semibold uppercase tracking-tight text-ink md:text-5xl">
                  {t('home.hero.subtitle')}
                </span>
              </h1>

              <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-muted md:text-xl">
                {t('home.hero.description')}{' '}
                <span className="font-medium text-ink">{t('home.hero.descriptionHighlight')}</span>{' '}
                {t('home.hero.descriptionEnd')}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={`/login?franchiseId=${MONKEY_FRANCHISE_ID}&redirect=/take-ticket`}>
                  <Button variant="primary" size="lg" className="gap-2">
                    {t('home.hero.bookNow')}
                    <ArrowRight className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </Link>
                <Link to={`/login?franchiseId=${MONKEY_FRANCHISE_ID}`}>
                  <Button variant="outline" size="lg">
                    {t('home.hero.professionalArea')}
                  </Button>
                </Link>
              </div>

              {/* Social proof — numbers are ink, never yellow */}
              <div className="mt-12 flex flex-wrap gap-x-8 gap-y-6 divide-x divide-hairline">
                <div className="first:pl-0 sm:pl-8">
                  <div className="text-3xl font-semibold tracking-tight tabular-nums text-ink md:text-4xl">
                    {clientsLabel}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-wide text-ink-subtle">
                    {t('home.hero.stats.clients')}
                  </div>
                </div>
                <div className="pl-8">
                  <div className="text-3xl font-semibold tracking-tight tabular-nums text-ink md:text-4xl">
                    {stats.avgRating.toFixed(1)}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-wide text-ink-subtle">
                    {t('home.hero.stats.rating')}
                  </div>
                </div>
                <div className="pl-8">
                  <div className="text-3xl font-semibold tracking-tight tabular-nums text-ink md:text-4xl">
                    {stats.avgWaitTime}<span className="text-xl text-ink-subtle md:text-2xl">min</span>
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-wide text-ink-subtle">
                    {t('home.hero.stats.averageWait')}
                  </div>
                </div>
              </div>
            </div>

            {/* Schedule panel — the product moment (Linear-style framed card) */}
            <div className="rounded-xl border border-hairline bg-surface p-6 md:p-7">
              <div className="mb-5 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-sans text-lg font-semibold tracking-tight text-ink">
                  <Clock className="h-5 w-5 text-ink-subtle" aria-hidden="true" />
                  {t('home.hero.schedule.title')}
                </h2>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2.5 py-1 text-xs font-medium text-success">
                  <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden="true" />
                  Tiempo real
                </span>
              </div>

              {/* Branch selector */}
              <div className="mb-5">
                <label className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-ink-subtle">
                  <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                  Sucursal
                </label>
                {branches.length > 1 ? (
                  <select
                    value={selectedBranch?.branchId || ''}
                    onChange={(e) => setSelectedBranchId(e.target.value)}
                    className="w-full rounded-lg border border-hairline bg-surface-3 px-4 py-2.5 font-medium text-ink outline-none focus:border-transparent focus:ring-2 focus:ring-focus"
                  >
                    {branches.map(branch => (
                      <option key={branch.branchId} value={branch.branchId}>
                        {branch.name} — {branch.city}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="rounded-lg border border-hairline bg-surface-3 px-4 py-2.5 font-medium text-ink">
                    {selectedBranch ? `${selectedBranch.name} — ${selectedBranch.city}` : 'Cargando…'}
                  </div>
                )}
              </div>

              {/* Schedule list — today highlighted, open = success, closed = subtle */}
              <div className="border-t border-hairline">
                {Object.entries(scheduleDisplay).map(([day, data]) => {
                  const isToday = day === todayKey;
                  return (
                    <div
                      key={day}
                      className={`flex items-center justify-between border-b border-hairline py-2.5 text-sm ${
                        isToday ? '-mx-3 rounded-lg bg-surface-2 px-3' : ''
                      }`}
                    >
                      <span className={`flex items-center gap-2 ${isToday ? 'font-medium text-ink' : 'text-ink-muted'}`}>
                        {isToday && <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />}
                        {data.label}
                      </span>
                      <span className={`tabular-nums ${isToday ? 'font-medium text-ink' : data.isOpen ? 'text-ink-muted' : 'text-ink-subtle'}`}>
                        {data.time}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Problem / Solution ─────────────────────────────────── */}
      <section className="border-b border-hairline bg-surface py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2 md:gap-8">
            {/* Problem */}
            <div className="rounded-xl border border-hairline bg-canvas p-8">
              <p className="text-xs font-medium uppercase tracking-[0.22em] text-ink-subtle">
                {t('home.problem.title')}
              </p>
              <ul className="mt-6 space-y-4 text-base text-ink-muted md:text-lg">
                {['home.problem.boring', 'home.problem.uncertain', 'home.problem.calling'].map((key) => (
                  <li key={key} className="flex items-start gap-3">
                    <span className="mt-2 h-px w-4 shrink-0 bg-hairline-strong" aria-hidden="true" />
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Solution */}
            <div className="rounded-xl border border-hairline bg-canvas p-8">
              <p className="text-xs font-medium uppercase tracking-[0.22em] text-accent">
                {t('home.solution.title')}
              </p>
              <ul className="mt-6 space-y-4 text-base text-ink md:text-lg">
                <li className="flex items-start gap-3">
                  <Check className="mt-1 h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
                  <span><strong className="font-semibold">{t('home.solution.booking')}</strong> <span className="text-ink-muted">{t('home.solution.bookingDesc')}</span></span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-1 h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
                  <span><strong className="font-semibold">{t('home.solution.notifications')}</strong> <span className="text-ink-muted">{t('home.solution.notificationsDesc')}</span></span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-1 h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
                  <span><strong className="font-semibold">{t('home.solution.onTime')}</strong><span className="text-ink-muted">{t('home.solution.onTimeDesc')}</span></span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ───────────────────────────────────────── */}
      <section className="border-b border-hairline bg-canvas py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="mx-auto max-w-6xl">
            <SectionHeading
              eyebrow={t('home.howItWorks.subtitle')}
              title={t('home.howItWorks.title')}
            />
            <div className="mt-14 grid gap-6 md:grid-cols-3 md:gap-8">
              {steps.map(({ num, Icon, titleKey, descKey }) => (
                <div
                  key={num}
                  className="group rounded-xl border border-hairline bg-surface p-7 transition-colors hover:border-hairline-strong"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-sans text-2xl font-semibold tracking-tight text-ink-subtle">{num}</span>
                    <Icon className="h-6 w-6 text-ink-subtle transition-colors group-hover:text-accent" aria-hidden="true" />
                  </div>
                  <h3 className="mt-6 font-sans text-xl font-semibold tracking-tight text-ink">
                    {t(titleKey)}
                  </h3>
                  <p className="mt-2 leading-relaxed text-ink-muted">{t(descKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Services ───────────────────────────────────────────── */}
      <section className="border-b border-hairline bg-surface py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="mx-auto max-w-6xl">
            <SectionHeading
              eyebrow={t('home.services.subtitle')}
              title={t('home.services.title')}
              subtitle={selectedBranch ? selectedBranch.name : undefined}
            />

            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {services.length > 0 ? (
                services.map((service) => {
                  const Icon = getServiceIcon(service);
                  return (
                    <div
                      key={service.serviceId}
                      className="group overflow-hidden rounded-xl border border-hairline bg-canvas transition-colors hover:border-accent/60"
                    >
                      {service.imageUrl ? (
                        <div className="h-40 overflow-hidden border-b border-hairline">
                          <img
                            src={service.imageUrl}
                            alt={service.name}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="flex h-40 items-center justify-center border-b border-hairline bg-surface-2">
                          <Icon className="h-10 w-10 text-ink-subtle" aria-hidden="true" />
                        </div>
                      )}
                      <div className="p-6">
                        <h3 className="font-sans text-lg font-semibold tracking-tight text-ink">{service.name}</h3>
                        {service.description && (
                          <p className="mt-2 line-clamp-2 text-sm text-ink-muted">{service.description}</p>
                        )}
                        <div className="mt-5 flex items-center justify-between border-t border-hairline pt-4">
                          <span className="text-2xl font-semibold tabular-nums text-ink">
                            {(service.price / 100).toFixed(0)}€
                          </span>
                          <span className="text-sm text-ink-subtle">{service.duration} min</span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                // Loading skeleton
                Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="rounded-xl border border-hairline bg-canvas p-6">
                    <div className="mb-4 h-12 w-12 rounded-lg bg-surface-2" />
                    <div className="mb-2 h-5 w-3/4 rounded bg-surface-2" />
                    <div className="mb-4 h-4 w-full rounded bg-surface-2" />
                    <div className="h-7 w-1/3 rounded bg-surface-2" />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────────── */}
      <section className="border-b border-hairline bg-canvas py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map(({ Icon, titleKey, descKey }) => (
              <div
                key={titleKey}
                className="group rounded-xl border border-hairline bg-surface p-6 transition-colors hover:border-hairline-strong"
              >
                <Icon className="h-6 w-6 text-ink-subtle transition-colors group-hover:text-accent" aria-hidden="true" />
                <h3 className="mt-5 font-sans text-base font-semibold tracking-tight text-ink">{t(titleKey)}</h3>
                <p className="mt-1.5 text-sm text-ink-muted">{t(descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About ──────────────────────────────────────────────── */}
      <section className="border-b border-hairline bg-surface py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="mx-auto max-w-6xl">
            <SectionHeading
              eyebrow={t('home.about.subtitle')}
              title={t('home.about.title')}
            />

            <div className="mt-14 grid items-start gap-12 md:grid-cols-2 md:gap-16">
              <div>
                <OptimizedImage
                  src="/logo_w_350.png"
                  alt="Monkey Barber Logo"
                  className="h-20 w-auto md:h-24"
                />
                <h3 className="mt-8 font-sans text-2xl font-semibold leading-snug tracking-tight text-ink md:text-3xl">
                  {t('home.about.heading')}{' '}
                  <span className="text-accent">{t('home.about.headingHighlight')}</span>
                </h3>
                <div className="mt-6 space-y-4 text-base leading-relaxed text-ink-muted md:text-lg">
                  <p>
                    {t('home.about.paragraph1')}{' '}
                    <strong className="font-medium text-ink">{t('home.about.paragraph1Highlight')}</strong>
                    {t('home.about.paragraph1End')}
                  </p>
                  <p>{t('home.about.paragraph2')}</p>
                  <p>
                    {t('home.about.paragraph3Start')}{' '}
                    <strong className="font-medium text-ink">{t('home.about.paragraph3Clients')}</strong>{' '}
                    {t('home.about.paragraph3Middle')}{' '}
                    <strong className="font-medium text-ink">{t('home.about.paragraph3Rating')}</strong>
                    {t('home.about.paragraph3End')}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {[
                  { Icon: Target, titleKey: 'home.about.mission.title', body: <p className="text-ink-muted">{t('home.about.mission.description')}</p> },
                  {
                    Icon: Sparkles,
                    titleKey: 'home.about.values.title',
                    body: (
                      <ul className="space-y-2 text-ink-muted">
                        {[
                          ['home.about.values.excellence', 'home.about.values.excellenceDesc'],
                          ['home.about.values.innovation', 'home.about.values.innovationDesc'],
                          ['home.about.values.respect', 'home.about.values.respectDesc'],
                          ['home.about.values.community', 'home.about.values.communityDesc'],
                        ].map(([title, desc]) => (
                          <li key={title} className="flex items-start gap-2.5">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                            <span><strong className="font-medium text-ink">{t(title)}</strong> {t(desc)}</span>
                          </li>
                        ))}
                      </ul>
                    ),
                  },
                  { Icon: Zap, titleKey: 'home.about.technology.title', body: <p className="text-ink-muted">{t('home.about.technology.description')}</p> },
                ].map(({ Icon, titleKey, body }) => (
                  <div key={titleKey} className="rounded-xl border border-hairline bg-canvas p-6">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-hairline bg-surface-2 text-ink-subtle">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <h4 className="font-sans text-lg font-semibold tracking-tight text-ink">{t(titleKey)}</h4>
                    </div>
                    <div className="mt-4">{body}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats — neutral ink numbers on hairline tiles */}
            <div className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-hairline bg-hairline md:grid-cols-4">
              {[
                { value: '2025', labelKey: 'home.about.stats.founded' },
                { value: `${stats.totalBarbers}+`, labelKey: 'home.about.stats.barbers' },
                {
                  value: stats.totalClients >= 1000 ? `${(stats.totalClients / 1000).toFixed(1)}K+` : `${stats.totalClients}+`,
                  labelKey: 'home.about.stats.clients',
                },
                { value: '98%', labelKey: 'home.about.stats.satisfaction' },
              ].map(({ value, labelKey }) => (
                <div key={labelKey} className="bg-canvas p-6 text-center">
                  <div className="text-3xl font-semibold tracking-tight tabular-nums text-ink md:text-4xl">{value}</div>
                  <div className="mt-1.5 text-xs uppercase tracking-wide text-ink-subtle">{t(labelKey)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Opportunities (Work with us / Franchise) ───────────── */}
      <section className="border-b border-hairline bg-canvas py-20 md:py-28">
        <div className="container mx-auto px-6">
          <div className="mx-auto max-w-4xl">
            <SectionHeading
              eyebrow={t('home.opportunities.subtitle', 'Dos formas de formar parte de nuestra familia')}
              title={t('home.opportunities.title', 'Únete a Monkey Barber')}
            />

            {/* Tabs */}
            <div className="mt-10 flex justify-center">
              <div className="inline-flex rounded-full border border-hairline bg-surface p-1">
                {([
                  { id: 'job', Icon: Briefcase, label: t('home.opportunities.tabs.job', 'Trabaja con Nosotros') },
                  { id: 'franchise', Icon: Store, label: t('home.opportunities.tabs.franchise', 'Abre tu Franquicia') },
                ] as const).map(({ id, Icon, label }) => (
                  <button
                    key={id}
                    onClick={() => setActiveOpportunityTab(id)}
                    className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                      activeOpportunityTab === id
                        ? 'bg-accent text-on-accent'
                        : 'text-ink-muted hover:text-ink'
                    }`}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Job tab */}
            {activeOpportunityTab === 'job' && (
              <>
                <div className="mt-10 grid gap-4 md:grid-cols-2">
                  {[
                    { Icon: Users, titleKey: 'home.joinTeam.team.title', descKey: 'home.joinTeam.team.description', titleFb: 'Equipo Profesional', descFb: 'Trabaja junto a los mejores barberos de la ciudad en un ambiente profesional y motivador.' },
                    { Icon: Sparkles, titleKey: 'home.joinTeam.growth.title', descKey: 'home.joinTeam.growth.description', titleFb: 'Crecimiento', descFb: 'Oportunidades de desarrollo profesional, formación continua y carrera dentro de la empresa.' },
                    { Icon: Briefcase, titleKey: 'home.joinTeam.tools.title', descKey: 'home.joinTeam.tools.description', titleFb: 'Herramientas', descFb: 'Tecnología de vanguardia y las mejores herramientas para que puedas dar lo mejor de ti.' },
                    { Icon: Heart, titleKey: 'home.joinTeam.culture.title', descKey: 'home.joinTeam.culture.description', titleFb: 'Cultura', descFb: 'Un ambiente de trabajo único donde la pasión por el oficio se combina con el buen rollo.' },
                  ].map(({ Icon, titleKey, descKey, titleFb, descFb }) => (
                    <div key={titleKey} className="rounded-xl border border-hairline bg-surface p-6 transition-colors hover:border-hairline-strong">
                      <div className="flex items-start gap-4">
                        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-hairline bg-surface-2 text-ink-subtle">
                          <Icon className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <div>
                          <h3 className="font-sans text-lg font-semibold tracking-tight text-ink">{t(titleKey, titleFb)}</h3>
                          <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">{t(descKey, descFb)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-10 text-center">
                  <Button variant="primary" size="lg" className="gap-2" onClick={() => setShowJobModal(true)}>
                    <Briefcase className="h-5 w-5" aria-hidden="true" />
                    {t('home.joinTeam.button', 'Enviar mi Candidatura')}
                  </Button>
                  <p className="mt-4 text-sm text-ink-subtle">
                    {t('home.joinTeam.note', 'Buscamos barberos con experiencia y ganas de crecer')}
                  </p>
                </div>
              </>
            )}

            {/* Franchise tab */}
            {activeOpportunityTab === 'franchise' && (
              <>
                <div className="mt-10 grid gap-4 md:grid-cols-2">
                  {[
                    { Icon: Store, titleKey: 'home.franchise.brand.title', descKey: 'home.franchise.brand.description', titleFb: 'Marca Reconocida', descFb: 'Únete a una marca con identidad única y reconocimiento en el sector de la barbería urbana.' },
                    { Icon: TrendingUp, titleKey: 'home.franchise.model.title', descKey: 'home.franchise.model.description', titleFb: 'Modelo Probado', descFb: 'Sistema de gestión digital completo: app propia, cola inteligente, fidelización y más.' },
                    { Icon: Shield, titleKey: 'home.franchise.support.title', descKey: 'home.franchise.support.description', titleFb: 'Soporte Total', descFb: 'Formación inicial, soporte continuo, marketing centralizado y asesoramiento permanente.' },
                    { Icon: MapPin, titleKey: 'home.franchise.territory.title', descKey: 'home.franchise.territory.description', titleFb: 'Tu Territorio', descFb: 'Exclusividad territorial garantizada. Elige tu ciudad y emprende con respaldo.' },
                  ].map(({ Icon, titleKey, descKey, titleFb, descFb }) => (
                    <div key={titleKey} className="rounded-xl border border-hairline bg-surface p-6 transition-colors hover:border-hairline-strong">
                      <div className="flex items-start gap-4">
                        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-hairline bg-surface-2 text-ink-subtle">
                          <Icon className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <div>
                          <h3 className="font-sans text-lg font-semibold tracking-tight text-ink">{t(titleKey, titleFb)}</h3>
                          <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">{t(descKey, descFb)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-10 text-center">
                  <Button variant="primary" size="lg" className="gap-2" onClick={() => setShowFranchiseModal(true)}>
                    <Store className="h-5 w-5" aria-hidden="true" />
                    {t('home.franchise.button', 'Solicitar Información')}
                  </Button>
                  <p className="mt-4 text-sm text-ink-subtle">
                    {t('home.franchise.note', 'Emprende tu propio negocio con el respaldo de Monkey Barber')}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ── Final CTA ──────────────────────────────────────────── */}
      <section className="bg-surface py-24 md:py-32">
        <div className="container mx-auto px-6">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-sans text-4xl font-semibold tracking-tight text-ink md:text-6xl">
              {t('home.cta.title')} {t('home.cta.titleLine2')}
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-lg text-ink-muted md:text-xl">
              {t('home.cta.subtitle')}
            </p>
            <div className="mt-9 flex justify-center">
              <Link to="/take-ticket">
                <Button variant="primary" size="lg" className="gap-2 px-10">
                  {t('home.cta.button')}
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </Button>
              </Link>
            </div>
            <p className="mt-5 text-xs uppercase tracking-[0.18em] text-ink-subtle">
              {t('home.cta.features')}
            </p>
          </div>
        </div>
      </section>

      <PublicFooter />

      {/* Job Application Modal */}
      <JobApplicationModal
        isOpen={showJobModal}
        onClose={() => setShowJobModal(false)}
      />

      {/* Franchise Application Modal */}
      <FranchiseApplicationModal
        isOpen={showFranchiseModal}
        onClose={() => setShowFranchiseModal(false)}
      />
    </div>
  );
}
